// @ts-strict-ignore
import Form from 'antd/lib/form';
import FormItem from 'antd/lib/form/FormItem';
import Modal from 'antd/lib/modal/Modal';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UpdateReleaseStepChecklistItemCommentRequest } from '../../actions/release';
import { ChecklistItemUpdatable } from '../../actions/updatable';
import { RootState } from '../../reducers/reducers';
import { selectAppTimeFormatSetting, selectEffectiveTimezone, selectOrgUser } from '../../selectors/selectors';
import { ChecklistItem, RunwayResponseType } from '../../types';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import { formatDateWithTimezone } from '../../timeHelpers';
import Space from 'antd/lib/space';
import { displayUserNameShort } from '../../helpers/displayHelpers';
import MarkdownEditor from '../HOCs/MarkdownEditor';
import { globalMessage } from '../../utils/globalMessage';
import { tryUpdateChecklistItem } from '../../actions/checklistItems';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';
import ChecklistItemImageUpload from './ChecklistItemImageUpload';
import { useChecklistImageUpload } from '@/utils/hooks/useChecklistImageUpload';

interface AddCommentComponentProps extends ReleaseComponentProps {
    open: boolean;
    item?: ChecklistItem;
    onCancel: () => void;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const AddCommentComponent: React.FC<AddCommentComponentProps> = (props: AddCommentComponentProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const stepRunId = props.stepRunComponentProps?.stepRunId;
    const { images, setImages, isUploadingImages, uploadNewImages, loadExistingAttachments, resetImages } =
        useChecklistImageUpload({
            orgId: props.orgId,
            appId: props.appId,
            commentTarget: stepRunId && props.item?.id ? { stepRunId, checklistItemId: props.item.id } : undefined,
        });

    const userId = useSelector((state: RootState) => state.user.id);
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));
    const itemId = props.item?.id;

    const [
        updateChecklistItemComment,
        updateChecklistItemCommentIsLoading,
        updateChecklistItemCommentResponseType,
        updateChecklistItemCommentError,
    ] = useCallUpdatable(
        StepRunUpdatable.UpdateChecklistItemComment,
        {
            ...props.stepRunComponentProps,
            checklistItemId: itemId,
        },
        itemId,
    );

    const releaseStepIsLoading = useIsLoading(ChecklistItemUpdatable.ChecklistItemCommentChanged, props.item?.id);
    const releaseStepCommentSuccess = useUpdated(ChecklistItemUpdatable.ChecklistItemCommentChanged, props.item?.id);
    const releaseStepCommentError = useError(ChecklistItemUpdatable.ChecklistItemCommentChanged, props.item?.id);

    const isLoading = props.stepRunComponentProps ? updateChecklistItemCommentIsLoading : releaseStepIsLoading;
    const commentSuccess =
        props.stepRunComponentProps ?
            updateChecklistItemCommentResponseType === RunwayResponseType.Success
        :   releaseStepCommentSuccess;
    const commentError = props.stepRunComponentProps ? updateChecklistItemCommentError : releaseStepCommentError;

    const isEditing = !!props.item?.comment;

    const updatedByUser = useSelector((state: RootState) => {
        if (props.item?.comment?.createdBy) {
            return selectOrgUser(state, props.orgId, props.item.comment.createdBy);
        } else {
            return null;
        }
    });

    const onOk = () => {
        form.validateFields()
            .then(async (values) => {
                const comment = values.comment;
                const allAttachments = await uploadNewImages();

                const request: UpdateReleaseStepChecklistItemCommentRequest = {
                    orgId: props.orgId,
                    appId: props.appId,
                    releaseId: props.releaseId,
                    checklistSettingId: props.item.id,
                    userId,
                    comment,
                    attachments: allAttachments.length > 0 ? allAttachments : undefined,
                };

                if (props.stepRunComponentProps) {
                    updateChecklistItemComment({
                        ...request,
                        comment: comment ?? '',
                    });
                } else {
                    dispatch(
                        tryUpdateChecklistItem(
                            ChecklistItemUpdatable.ChecklistItemCommentChanged,
                            request,
                            props.item.id,
                        ),
                    );
                }
            })
            .catch((e) => globalMessage.error(e.message));
    };

    React.useEffect(() => {
        if (commentSuccess) {
            globalMessage.success('Updated comment!');

            props.onCancel();
        }
    }, [commentSuccess]);

    React.useEffect(() => {
        if (commentError) {
            globalMessage.error(commentError);
        }
    }, [commentError]);

    React.useEffect(() => {
        if (props.item) {
            form.setFieldsValue({ comment: props.item?.comment?.comment ?? '' });

            loadExistingAttachments(props.item?.comment?.attachments);
        }
    }, [props.item?.id]);

    return (
        <Modal
            title={isEditing ? 'Edit comment' : 'Add comment'}
            open={props.open}
            confirmLoading={isLoading || isUploadingImages}
            okButtonProps={{ loading: isLoading || isUploadingImages }}
            okText={isEditing ? 'Update' : 'Save'}
            onCancel={props.onCancel}
            onOk={() => form.submit()}
            afterClose={() => {
                form.resetFields();
                resetImages();
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text type="secondary">Commenting on item: {props.item?.title}</Text>
                <Form
                    form={form}
                    name="beta"
                    layout="vertical"
                    initialValues={{ comment: '' }}
                    onFinish={onOk}
                    style={{ marginTop: 24 }}
                >
                    <FormItem label="Comment" name="comment">
                        <MarkdownEditor preview="edit" />
                    </FormItem>
                    {!!props.stepRunComponentProps && (
                        <ChecklistItemImageUpload images={images} setImages={setImages} />
                    )}
                </Form>
                <Space direction="vertical">
                    {props.item?.comment?.createdAt && (
                        <Text type="secondary">
                            Last updated on:{' '}
                            {formatDateWithTimezone(
                                props.item.comment.createdAt,
                                'short',
                                effectiveTimezone,
                                use24HourTimeFormat,
                            )}
                            {updatedByUser && ` by ${displayUserNameShort(updatedByUser)}`}
                        </Text>
                    )}
                </Space>
            </div>
        </Modal>
    );
};

export default AddCommentComponent;
